import {
    createDepositPaymentCheckout,
    registerReservationCashPayment,
    sendReservationPaymentLink,
    PAYMENT_METHOD_VALUES,
} from "./reservationPayment";
import { getCompanyId } from "../../insurance/lib/insuranceSaleApi";
import { isSolaGateway, sendSolaPaymentLink } from "../../../lib/sola/solaPaymentApi";
import { isSolaPaymentCancelled } from "../../../components/payment/useSolaCardPayment";

/**
 * Extension payments return to the details page with `extensionPayment` and no `resID`, so the
 * page's scheduled-payment handler (`payment-status`) stays out of it: the Stripe webhook applies
 * the new drop-off and invoice from the session metadata.
 */
const buildExtensionPaymentUrls = ({ reservationId, reservation, companyId, extension }) => {
    const detailsBase = `${window.location.origin}/reservations/${reservationId}`;
    const context = extension.invoiceContext || {};

    return {
        successUrl: `${detailsBase}?extensionPayment=true&paymentSuccess=true&paymentPlatform=stripe`,
        cancelUrl: `${detailsBase}?extensionPayment=true&paymentSuccess=false&paymentPlatform=stripe`,
        paymentMetadata: {
            reservationProcess: "extension",
            reservationId,
            email: reservation?.email,
            companyId,
            extensionDropoffDate: extension.dropoffDate,
            // The checkout builder reads `categoryId`, the webhook reads `category`; send both.
            category: context.category || "",
            categoryId: context.category || "",
            carId: context.carId || "",
            locationId: context.locationId || "",
            addonIdList: context.addonIdList || [],
            insuranceId: context.insuranceId || "",
        },
    };
};

const buildDuePaymentUrls = ({ reservationId, dueAmount, reservation, companyId, extension }) => {
    if (extension) {
        return buildExtensionPaymentUrls({ reservationId, reservation, companyId, extension });
    }

    const { origin } = window.location;
    const detailsBase = `${origin}/reservations/${reservationId}`;

    const successUrl = `${detailsBase}?success=true&resID=${reservationId}&amount=${(dueAmount * 100).toFixed()}&paymentSuccess=true&isDeposit=true&paymentPlatform=stripe&reservationProcess=scheduled`;
    const cancelUrl = `${detailsBase}?success=false&paymentSuccess=false&paymentPlatform=stripe`;

    const paymentMetadata = {
        reservationProcess: "scheduled",
        reservationId,
        email: reservation?.email,
        companyId,
        carId: reservation?.carSelected?._id || reservation?.carSelected?.id || reservation?.carLinked?.[0]?._id || "",
        locationId:
            typeof reservation?.pickupLocation === "string"
                ? reservation.pickupLocation
                : reservation?.pickupLocation?._id || reservation?.pickupLocation?.id || "",
        category:
            typeof reservation?.category === "string"
                ? reservation.category
                : reservation?.category?._id || reservation?.category?.id || "",
    };

    return { successUrl, cancelUrl, paymentMetadata };
};

async function solaDuePayment({
    reservation,
    dueAmount,
    paymentSystem,
    companyData, toast,
    t,
    requestSolaCardPayment,
    extension,
}) {
    const reservationId = reservation?.id;
    const companyId = getCompanyId(companyData);
    const customerEmail = reservation?.email;
    const selectedPayment = paymentSystem || companyData?.defaultPaymentGateway || "stripe";
    const reservationProcess = extension ? "extension" : "scheduled";
    const extensionDropoffDate = extension?.dropoffDate;

    if (selectedPayment === PAYMENT_METHOD_VALUES.SEND_LINK) {
        await sendSolaPaymentLink({
            reservationId,
            reservationProcess,
            amount: dueAmount,
            customerEmail,
            companyId,
            extensionDropoffDate,
        });
        toast.success({
            message: t("reservationPayment:sendLink.successTitle"),
            description: t("reservationPayment:sendLink.successDescription"),
        });
        return { type: "link_sent" };
    }

    try {
        await requestSolaCardPayment({
            reservationId,
            reservationProcess,
            amount: dueAmount,
            customerEmail,
            companyId,
            extensionDropoffDate,
        });
    } catch (error) {
        if (isSolaPaymentCancelled(error)) {
            return { type: "cancelled" };
        }
        throw error;
    }

    return { type: "completed" };
}

/**
 * Collects a balance on a reservation by cash, card or emailed link.
 *
 * Pass `extension: { dropoffDate, invoiceContext }` to collect an extension instead: the payment
 * is tagged `reservationProcess: "extension"` and the backend moves the drop-off once it clears.
 * Cash extensions don't come through here — they go to `finalizeReservationExtension`, which
 * records the cash and extends in one call.
 */
async function confirmDuePaymentFlow({
    reservation,
    dueAmount,
    paymentSystem,
    companyData, toast,
    t,
    requestSolaCardPayment,
    extension,
}) {
    const reservationId = reservation?.id;
    if (!reservationId) {
        throw new Error("Missing reservation id");
    }

    const normalizedDue = Number(dueAmount);
    if (!normalizedDue || normalizedDue <= 0) {
        throw new Error("No balance due");
    }

    const selectedPayment = paymentSystem || companyData?.defaultPaymentGateway || "stripe";

    if (selectedPayment === PAYMENT_METHOD_VALUES.CASH) {
        if (extension) {
            throw new Error("Cash extensions are finalized with finalizeReservationExtension");
        }
        await registerReservationCashPayment(reservationId, normalizedDue, "scheduled");
        return { type: "completed" };
    }

    const customerEmail = reservation?.email;
    if (!customerEmail) {
        throw new Error("Missing customer email for payment");
    }

    if (isSolaGateway(companyData)) {
        if (typeof requestSolaCardPayment !== "function") {
            throw new Error("Sola card payment is not available");
        }
        return solaDuePayment({
            reservation,
            dueAmount: normalizedDue,
            paymentSystem,
            companyData,
toast,
            t,
            requestSolaCardPayment,
            extension,
        });
    }

    const companyId = getCompanyId(companyData);
    const { successUrl, cancelUrl, paymentMetadata } = buildDuePaymentUrls({
        reservationId,
        dueAmount: normalizedDue,
        reservation,
        companyId,
        extension,
    });

    const checkout = await createDepositPaymentCheckout({
        reservationId,
        amount: normalizedDue,
        customerEmail,
        successUrl,
        cancelUrl,
        metadata: paymentMetadata,
        companyId,
    });

    const paymentUrl = checkout?.paymentUrl;
    if (!paymentUrl) {
        throw new Error("Payment URL not received");
    }

    if (selectedPayment === PAYMENT_METHOD_VALUES.SEND_LINK) {
        await sendReservationPaymentLink({
            reservationId,
            customerEmail,
            paymentUrl,
            companyId,
        });
        toast.success({
            message: t("reservationPayment:sendLink.successTitle"),
            description: t("reservationPayment:sendLink.successDescription"),
        });
        return { type: "link_sent" };
    }

    window.location.href = paymentUrl;
    return { type: "redirect" };
}

export default confirmDuePaymentFlow;
